import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { EmailChangeEvent, ProfileEditService } from './profile-edit.service';
import { Popr2Service, Popr2Level } from '@ciam/library';
import { VerifyPinComponent } from './verify-pin/verify-pin.component';

@Component({
  selector: 'wpro-profile-edit',
  templateUrl: './profile-edit.component.html',
  standalone: false
})
export class ProfileEditComponent implements OnInit, OnDestroy {

  emailChanged = false;
  originalEmailId = '';
  newEmailId = '';
  source: 'login' | 'contact' | null = null;

  @Output() returnToView = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  constructor(
    private profileEditService: ProfileEditService,
    private popr2Service: Popr2Service
  ) {}

  ngOnInit(): void {
    // Subscribe to email change events from both LoginComponent and EditContactComponent
    this.profileEditService.emailChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event) {
          this.handleEmailChange(event);
        } else {
          this.reset();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleEmailChange(event: EmailChangeEvent): void {
    this.emailChanged = true;
    this.originalEmailId = event.originalEmailId;
    this.newEmailId = event.newEmailId;
    this.source = event.source;    
    
    // Open verify-profile-edit popup using popr2Service
    this.openVerifyProfileEditPopup(event);
  }

  openVerifyProfileEditPopup(event: EmailChangeEvent): void {
    const modalRef = this.popr2Service.createModal(
      VerifyPinComponent,
      {
        priority: Popr2Level.NONE,
        dimension: '.fdx-c-dialog--narrow.fdx-c-dialog--condensed'
      }
    );

    let componentInstance: VerifyPinComponent;

    modalRef.open((comp: VerifyPinComponent) => {
      // Store component reference
      componentInstance = comp;
      // Pass data to the component
      comp.originalEmailId = event.originalEmailId;
      comp.newEmailId = event.newEmailId;
      comp.source = event.source;
      // Pass modal reference so component can close itself
      comp.modalRef = modalRef;
      // Programmatic closes (cancel/backdrop/verified/change-email) do not fire registerOnClose.
      comp.closed.pipe(take(1)).subscribe(() => this.finalizePopupClose(comp));
    });

    // registerOnClose only fires for the library ✕ button; programmatic closes are handled above.
    modalRef.registerOnClose(() => this.finalizePopupClose(componentInstance));
  }

  private finalizePopupClose(componentInstance?: VerifyPinComponent): void {
    const changeEmailRequested = !!componentInstance?.changeEmailRequested;
    // Clear the pending email-change event on success and cancellation alike, so a
    // later ProfileEdit subscriber does not replay it and reopen the popup.
    this.profileEditService.clearEmailChange();
    this.reset();
    // Return to the read-only view on success and on ✕/outside-click dismissals;
    // stay on the edit form only when the user chose "Change email address".
    if (!changeEmailRequested) {
      window.scroll(0, 0);
      this.returnToView.emit();
    }
  }

  reset(): void {
    this.emailChanged = false;
    this.originalEmailId = '';
    this.newEmailId = '';
    this.source = null;
  }
}
